/**
 * Diagnostic decoding of an access token's non-secret claims.
 *
 * Exists because a Graph 401 has two completely different causes that the HTTP status alone cannot
 * separate, and guessing between them costs an Azure-portal round trip each time:
 *
 *  - `aud` is not Graph → the token is for the wrong resource entirely (e.g. the app's own
 *    `api://{clientId}/API.Access`), so no permission grant will ever fix it.
 *  - `aud` IS Graph but `scp` lacks the scope → the delegated permission is not granted on the app
 *    registration that issued this token, and AAD dropped it silently rather than failing.
 *
 * `tid`/`appid` matter here specifically because this client holds TWO app registrations across TWO
 * dev tenants: they say which registration and which tenant actually minted the token, which is how a
 * permission granted on the wrong one gets caught.
 *
 * NEVER logs or returns the token itself, and deliberately omits `upn`/`oid`/`name` — the four claims
 * below are all that any of the above needs, and none of them identifies a person.
 *
 * No signature verification, by design: this is not a security decision, it is a printf. Only the
 * resource server may validate a token.
 */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  const segments = token.split('.');
  if (segments.length < 2) return null;

  try {
    // base64url → base64, then re-pad to a multiple of 4 for atob.
    const base64 = segments[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
    // atob mangles multi-byte UTF-8, which is fine: every claim read below is ASCII, and a mangled
    // body can only make JSON.parse throw, which is caught.
    return JSON.parse(atob(padded)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

// Only used for error diagnostics, so no need to type the claims more strictly than "unknown".
export function describeTokenClaims(token: string | undefined): string {
  if (!token)
    return 'no token was supplied (Authorization would have been "Bearer undefined")';

  const claims = decodeJwtPayload(token);
  if (!claims) return 'token is not a decodable JWT';

  return [
    `aud=${claims.aud ?? '?'}`,
    `scp=${claims.scp ?? '(none)'}`,
    `tid=${claims.tid ?? '?'}`,
    `appid=${claims.appid ?? '?'}`,
  ].join(' ');
}

/** True when `aud` names Microsoft Graph, by either the URL or the well-known resource id. */
const GRAPH_AUDIENCES = new Set([
  'https://graph.microsoft.com',
  'https://graph.microsoft.com/',
  '00000003-0000-0000-c000-000000000000',
]);

export function isGraphAudience(token: string | undefined): boolean {
  if (!token) return false;
  const aud = decodeJwtPayload(token)?.aud;
  return typeof aud === 'string' && GRAPH_AUDIENCES.has(aud);
}
